/* =============================================================================
   CORE / BUILD-INSTRUCTIONS — Bauanleitung je Bestellposition (S154 Phase C)
   =============================================================================
   Pure-Function: bestellungsgenaue Aufbau-Reihenfolge eines Gerichts für KDS/Theke.
   Effektive Zutaten = dieselbe A2-Komposition (`composeLines` aus nutrition.js),
   damit Bauanleitung + Nährwerte deckungsgleich sind:
     - Basis − abgewählte (als kind:'remove' MIT in der Liste, „ohne …")
     - Bun/Patty (gewählt ?? Default), Dressing, Side
     - Extras/Sauces als kind:'add' („+ …")
   Reihenfolge (Aufbau von unten nach oben):
     step_no falls am recipe_item gepflegt, sonst Kategorie-Rang (Default-Map),
     stabil nach sort_order. Schritt-Text = step_text falls gesetzt, sonst generisch.
   Vollständig restaurant-individuell: step_no/step_text/station kommen aus dem
   Rezept-Editor (menu-recipe-editor.js).
============================================================================ */

import { composeLines } from './nutrition.js';

// Kategorie-Rang (klein = zuerst belegt). Keyword-Match auf category/subcategory,
// damit es robust gegen leichte Benennungsvarianten ist. Reihenfolge der Regeln
// = Priorität (erste passende gewinnt). Unbekannt → 45.
const RANK_RULES = [
  { rank: 5,  kw: ['sättigung', 'sattigung', 'beilage', 'pommes', 'fries', 'reis', 'kartoffel', 'bowl-base', 'base'] },
  { rank: 10, kw: ['brot', 'bun', 'brötchen', 'brotchen', 'teig', 'wrap', 'fladen'] },
  { rank: 20, kw: ['fleisch', 'patty', 'rind', 'hähnch', 'hahnch', 'hühnch', 'huhn', 'pute', 'fisch', 'lachs', 'thunfisch', 'bacon', 'schinken', 'wurst', 'falafel', 'tofu', 'protein'] },
  { rank: 25, kw: ['ei ', 'eier', ' ei', 'spiegelei'] },
  { rank: 30, kw: ['käse', 'kase', 'cheese', 'cheddar', 'feta', 'mozzarella', 'gouda'] },
  { rank: 40, kw: ['gemüse', 'gemuse', 'salat', 'tomate', 'zwiebel', 'gurke', 'rucola', 'paprika', 'mais', 'jalapeno', 'jalapeño', 'avocado', 'spinat', 'kraut', 'lauch', 'pilz'] },
  { rank: 50, kw: ['nuss', 'nüsse', 'nusse', 'kern', 'mandel', 'obst', 'frucht', 'apfel', 'beere', 'granatapfel'] },
  { rank: 55, kw: ['süß', 'suss', 'zucker', 'honig', 'sirup', 'marmelade', 'dattel'] },
  { rank: 60, kw: ['soße', 'sosse', 'sauce', 'dressing', 'dip', 'mayo', 'ketchup', 'senf', 'aioli', 'pesto'] },
  { rank: 65, kw: ['gewürz', 'gewurz', 'salz', 'pfeffer', 'spice', 'chili', 'kräuter', 'krauter', 'gewuerz'] }
];
const RANK_UNKNOWN = 45;

// S154b — Bun/Brot-Erkennung (für den Burger-Stapel: Boden + Deckel bookenden).
const BUN_KW = ['brot', 'bun', 'brötchen', 'brotchen', 'brioche', 'semmel', 'wrap', 'fladen'];
function isBunIngredient(ing) {
  if (!ing) return false;
  const hay = (String(ing.category || '') + ' ' + String(ing.subcategory || '') + ' ' + String(ing.name || '')).toLowerCase();
  return BUN_KW.some(k => hay.includes(k));
}

function rankForIngredient(ing) {
  if (!ing) return RANK_UNKNOWN;
  const hay = (String(ing.category || '') + ' ' + String(ing.subcategory || '')).toLowerCase();
  for (const r of RANK_RULES) if (r.kw.some(k => hay.includes(k))) return r.rank;
  // Fallback: Name prüfen (nur wenn Kategorie nichts ergab)
  const nameHay = String(ing.name || '').toLowerCase();
  for (const r of RANK_RULES) if (r.kw.some(k => nameHay.includes(k))) return r.rank;
  return RANK_UNKNOWN;
}

function fmtQty(entry, ing) {
  const q = entry.quantity;
  if (q == null || !isFinite(q)) return '';
  if (entry.unit && entry.unit !== 'base') return `${q} ${entry.unit}`;
  const base = ing && ing.baseUnit;
  if (base === 'piece') return `${q} Stück`;
  if (base === 'ml') return `${q} ml`;
  return `${q} g`;
}

function defaultText(entry, ing, name) {
  if (entry.kind === 'remove') return 'ohne ' + name;
  const q = fmtQty(entry, ing);
  if (entry.kind === 'add') return '+ ' + name + (q ? ' (' + q + ')' : '');
  return q ? `${name} · ${q}` : name;
}

/**
 * Geordnete Bauanleitung einer Bestellposition.
 * @param {string} menuItemId
 * @param {object} customizations - { bun, patty, dressing, side, extras, sauces, removedIngredients }
 * @param {object} ctx - { recipesByItem, ingredientsById, defaults? }
 * @returns {Array<{order,name,qty,unit,station,kind,text}>}
 */
export function buildInstructionSteps(menuItemId, customizations, ctx = {}) {
  // S154c — Beilage (side) + Dip-Soßen (sideSauces) gehören NICHT in die Bauanleitung.
  const entries = composeLines(menuItemId, customizations, { ...ctx, forBuild: true });
  const ingredientsById = ctx.ingredientsById || {};

  const decorated = entries.map((e, idx) => {
    const ing = e.ingredientId ? ingredientsById[e.ingredientId] : null;
    const name = (ing && ing.name) || e.name || '—';
    const rank = (e.stepNo != null)
      ? e.stepNo
      : (ing ? rankForIngredient(ing) : (e.rankHint != null ? e.rankHint : RANK_UNKNOWN));
    return { e, ing, name, rank, sortOrder: e.sortOrder || 0, idx };
  });

  decorated.sort((a, b) => (a.rank - b.rank) || (a.sortOrder - b.sortOrder) || (a.idx - b.idx));

  const toStep = (d, layer, opts = {}) => ({
    order: 0,
    name: d.name,
    qty: ('qty' in opts) ? opts.qty : (d.e.quantity != null ? d.e.quantity : null),
    unit: ('unit' in opts) ? opts.unit : (d.e.unit || null),
    station: d.e.station || null,
    kind: d.e.kind,
    text: (opts.text != null) ? opts.text : (d.e.stepText ? d.e.stepText : defaultText(d.e, d.ing, d.name)),
    layer
  });

  // S154b — Burger-Stapel: einen vorhandenen (nicht abgewählten) Bun doppeln →
  // Boden (bottom, ganz vorn) + Deckel (top, ganz hinten); Füllung dazwischen als
  // middle (in der UI eingerückt). meta.isBurger erzwingt das Bookending auch, wenn
  // die Brot-Erkennung über die Kategorie nicht greift (dann der Brot-Rang-Schritt).
  const isBun = d => d.e.kind !== 'remove' && (isBunIngredient(d.ing) || (ctx.isBurger && d.ing && d.rank === 10));
  const bunIdx = decorated.findIndex(isBun);

  let out;
  if (bunIdx >= 0) {
    const bunD = decorated[bunIdx];
    const bottom = toStep(bunD, 'bottom', { text: bunD.e.stepText ? bunD.e.stepText : `${bunD.name} · Unterseite` });
    const top = toStep(bunD, 'top', { text: `${bunD.name} · Deckel`, qty: null, unit: null });
    const middle = decorated.filter((_, i) => i !== bunIdx).map(d => toStep(d, 'middle'));
    out = [bottom, ...middle, top];
  } else {
    out = decorated.map(d => toStep(d, 'middle'));
  }
  out.forEach((s, i) => { s.order = i + 1; });
  return out;
}
